import React, { FC, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import Classnames from 'classnames';

import { ButtonIcon, ModalService, Tooltip } from '@bluevalet/common-ui';

import ChevronIcon from '@bluevalet/react-icons/icon-chevron';
import CloseIcon from '@bluevalet/react-icons/icon-close';
import TickIcon from '@bluevalet/react-icons/icon-tick';

// Service Icons
import InsuranceIcon from '@bluevalet/react-icons/insurance';
import TirePressureIcon from '@bluevalet/react-icons/tire-pressure';
import CoveredParkingIcon from '@bluevalet/react-icons/covered-parking';
import WindscreenWasherIcon from '@bluevalet/react-icons/windscreen-washer';
import ElectricChargingIcon from '@bluevalet/react-icons/electric-charging';
import InsideWashIcon from '@bluevalet/react-icons/inside-wash';
import OutsideWashIcon from '@bluevalet/react-icons/outside-wash';
import IntegralWashIcon from '@bluevalet/react-icons/integral-wash';
import SupplementIcnIcon from '@bluevalet/react-icons/supplement-icn';
import DisinfectionPremiumIcon from '@bluevalet/react-icons/disinfection-premium';
import DisinfectionIcon from '@bluevalet/react-icons/disinfection';
import AutomaticCarWashIcon from '@bluevalet/react-icons/automatic-car-wash';
import OutdoorParkingIcon from '@bluevalet/react-icons/outdoor-parking';

import { Resource } from '../../../shared/modules/resource/model';
import { Image } from '@mantine/core';
import { Api } from '../../common/model';

interface Props extends WithTranslation {
  service: Api.Service;
  isEnabled: boolean;
  onToggleService: (option: number, previous: boolean) => void;
}

interface IconServiceProps {
  resource: Resource;
  size: number;
  disabled?: boolean;
}

// todo: remove this ugly mapping
const IconService: FC<IconServiceProps> = ({ resource, size, disabled = false }) => {
  switch (resource.name) {
    case 'asssurance.jpg':
      return <InsuranceIcon size={size} disabled={disabled} />;
    case 'pression-pneu.jpg':
      return <TirePressureIcon size={size} disabled={disabled} />;
    case 'parking-couvert.jpg':
      return <CoveredParkingIcon size={size} disabled={disabled} />;
    case 'lave-vitre.jpg':
      return <WindscreenWasherIcon size={size} disabled={disabled} />;
    case 'recharge-vehicule.jpg':
      return <ElectricChargingIcon size={size} disabled={disabled} />;
    case 'lavage-interieur.jpg':
      return <InsideWashIcon size={size} disabled={disabled} />;
    case 'lavage-exterieur.jpg':
      return <OutsideWashIcon size={size} disabled={disabled} />;
    case 'lavage-integral.jpg':
      return <IntegralWashIcon size={size} disabled={disabled} />;
    case 'dessablage.jpg':
      return <SupplementIcnIcon size={size} disabled={disabled} />;
    case 'VIREX.jpg':
      return <DisinfectionIcon size={size} disabled={disabled} />;
    case 'OZONETECH.jpg':
      return <DisinfectionPremiumIcon size={size} disabled={disabled} />;
    case 'lavage-rouleau.jpg':
      return <AutomaticCarWashIcon size={size} disabled={disabled} />;
    case 'parking-exterieur.jpg':
      return <OutdoorParkingIcon size={size} disabled={disabled} />;
    default:
      return <Image src={resource.url.web} alt={resource.name} width={size} height={size} />;
  }
};

const AvailableService: FC<Props> = ({ t, service, isEnabled, onToggleService }) => {
  const modalRef = useRef<ModalService>(null);
  const [isSelected, setSelected] = useState(false);

  useEffect(() => {
    setSelected(isEnabled);
  }, [isEnabled]);

  const toggleService = () => {
    if (!service.isDisabled) {
      setSelected(!isSelected);
      onToggleService(service.id, isSelected);
    }
  };

  const priceLabel = service.price === 0 ? t('included') : `${service.price}€`;
  const disabled = service.isDisabled;
  const clsService = Classnames('service', {
    disabled,
    selected: isSelected,
  });

  const openServiceModal: MouseEventHandler = event => {
    event.preventDefault();
    event.stopPropagation();
    modalRef.current?.openModal();
  };

  const toggleServiceLabel = isSelected ? t('booking-services_remove_service') : t('booking-services_add_service');

  return (
    <div className={clsService} onClick={toggleService} role="button" aria-pressed={isSelected} tabIndex={0}>
      <div className="informations">
        {service.image && <IconService resource={service.image} size={35} disabled={disabled} />}
        <h4>{service.label}</h4>
        {disabled && <Tooltip label={t('unavailable')} type="blue" />}
        <span role="presentation" className="see-more" onClick={openServiceModal}>
          <span>{t('booking-know_more')}</span>
          <ChevronIcon size={16} />
        </span>
      </div>
      <div className="choose">
        <span className="price">{priceLabel}</span>
        {isSelected ? (
          <ButtonIcon>
            <TickIcon size={18} />
          </ButtonIcon>
        ) : (
          <ButtonIcon disabled={service.isDisabled}>
            <CloseIcon size={18} />
          </ButtonIcon>
        )}
      </div>

      <ModalService
        ref={modalRef}
        icon={service.image ? <IconService resource={service.image} size={45} /> : null}
        name={service.label}
        price={priceLabel}
        description={service.description}
        isDisabled={disabled}
        buttonProps={
          service.enabledByDefault
            ? undefined
            : {
                label: toggleServiceLabel,
                handleClick: () => {
                  modalRef.current?.closeModal();
                  toggleService();
                },
              }
        }
      />
    </div>
  );
};

export default withTranslation()(AvailableService);
